import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, ChevronsUpDown, X, Layers } from "lucide-react";

const label = (a) => `${a.asset_id} — ${a.name}`;
const searchKey = (a) => [a.asset_id, a.name, a.model_number, a.location, a.id].filter(Boolean).join(" ");

export const machineTypesOf = (assets) => {
  const m = new Map();
  assets.forEach((a) => {
    const n = (a.name || "").trim();
    if (n) m.set(n, (m.get(n) || 0) + 1);
  });
  return [...m.entries()].sort((x, y) => x[0].localeCompare(y[0]));
};

function MachineRow({ a, checked, onSelect, testid, hint }) {
  return (
    <CommandItem value={searchKey(a)} onSelect={onSelect} data-testid={`${testid}-option-${a.asset_id}`}>
      <Check className={`mr-2 h-4 w-4 ${checked ? "opacity-100" : "opacity-0"}`} />
      <div className="flex-1 min-w-0">
        <div className="truncate"><span className="font-mono text-xs">{a.asset_id}</span> — {a.name}</div>
        <div className="text-[11px] text-slate-500 truncate">
          {[a.model_number && `Model ${a.model_number}`, a.location && `Line ${a.location}`].filter(Boolean).join(" · ") || "—"}
        </div>
      </div>
      {hint && <span className="text-[10px] text-emerald-700 ml-2">{hint}</span>}
    </CommandItem>
  );
}

/** Multi-select: specific machines and/or whole machine types (same machine name). */
export function MultiMachinePicker({ assets, ids, types, onChange, testid = "machine-picker" }) {
  const [open, setOpen] = useState(false);
  const typeList = useMemo(() => machineTypesOf(assets), [assets]);
  const toggleId = (id) => onChange({ ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id], types });
  const toggleType = (t) => onChange({ ids, types: types.includes(t) ? types.filter((x) => x !== t) : [...types, t] });
  const byId = Object.fromEntries(assets.map((a) => [a.id, a]));
  const count = ids.length + types.length;

  return (
    <div className="space-y-2">
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="w-full justify-between font-normal" data-testid={testid}>
            <span className="truncate text-slate-600">{count ? `${count} selected — add more...` : "Search machines by ID, name, model or line..."}</span>
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]" align="start">
          <Command>
            <CommandInput placeholder="Type to search..." data-testid={`${testid}-search`} />
            <CommandList className="max-h-80">
              <CommandEmpty>No machine found.</CommandEmpty>
              <CommandGroup heading="Machine types (all units with this name)">
                {typeList.map(([t, n]) => (
                  <CommandItem key={`t-${t}`} value={`type ${t}`} onSelect={() => toggleType(t)} data-testid={`${testid}-type-${t.replace(/\s+/g, "-").toLowerCase()}`}>
                    <Check className={`mr-2 h-4 w-4 ${types.includes(t) ? "opacity-100" : "opacity-0"}`} />
                    <Layers className="mr-2 h-3.5 w-3.5 text-slate-400" />
                    <span className="flex-1 truncate">All {t}</span>
                    <span className="text-[11px] text-slate-500">{n} unit{n > 1 ? "s" : ""}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandGroup heading="Specific machines">
                {assets.map((a) => (
                  <MachineRow key={a.id} a={a} checked={ids.includes(a.id)} onSelect={() => toggleId(a.id)} testid={testid} />
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {count > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-selected`}>
          {types.map((t) => (
            <Badge key={`t-${t}`} variant="outline" className="bg-amber-50 border-amber-300 text-amber-800 gap-1">
              All {t}
              <button type="button" onClick={() => toggleType(t)} data-testid={`${testid}-remove-type-${t.replace(/\s+/g, "-").toLowerCase()}`}><X className="w-3 h-3" /></button>
            </Badge>
          ))}
          {ids.map((id) => (
            <Badge key={id} variant="outline" className="gap-1">
              {byId[id] ? label(byId[id]) : id}
              <button type="button" onClick={() => toggleId(id)} data-testid={`${testid}-remove-${byId[id]?.asset_id || id}`}><X className="w-3 h-3" /></button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

/** Single-select with optional "fits" group listed first. */
export function SingleMachinePicker({ assets, value, onChange, fitIds, testid = "machine-picker-single" }) {
  const [open, setOpen] = useState(false);
  const fits = fitIds ? assets.filter((a) => fitIds.has(a.id)) : [];
  const others = fitIds ? assets.filter((a) => !fitIds.has(a.id)) : assets;
  const sel = assets.find((a) => a.id === value);
  const pick = (id) => { onChange(id); setOpen(false); };

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-between font-normal" data-testid={testid}>
          <span className="truncate">{sel ? label(sel) : <span className="text-slate-500">Search machine by ID, name, model or line...</span>}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]" align="start">
        <Command>
          <CommandInput placeholder="Type to search..." data-testid={`${testid}-search`} />
          <CommandList className="max-h-80">
            <CommandEmpty>No machine found.</CommandEmpty>
            {fits.length > 0 && (
              <CommandGroup heading="Fits this spare">
                {fits.map((a) => <MachineRow key={a.id} a={a} checked={value === a.id} onSelect={() => pick(a.id)} testid={testid} hint="fits" />)}
              </CommandGroup>
            )}
            <CommandGroup heading={fits.length ? "Other machines" : "Machines"}>
              {others.map((a) => <MachineRow key={a.id} a={a} checked={value === a.id} onSelect={() => pick(a.id)} testid={testid} />)}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
